import React, { Fragment, memo, useCallback, useMemo } from 'react'
import { PulseLoader } from 'react-spinners'
import styled from 'styled-components'
import ChatCard, { Chat } from './chatCard'
import { faThumbTack } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { MAX_FIXED_CHATS } from '~/components/llm/llmChatContext'

interface GroupedChats {
  [key: string]: Chat[]
}

const GroupTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 25px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  padding: 0 10px;

  &:first-of-type {
    margin-top: 0px;
  }
`

const FixedChatsCounter = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  padding: 3px 5px;
  border-radius: 4px;
  background-color: #f0f0f0;
`

const LoadingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #666;
`

const EmptyState = styled.div`
  padding: 16px;
  text-align: center;
  color: #666;
`

interface ChatListGroupedProps {
  chats: Chat[]
  currentChatId?: number
  loading: boolean
  listType: 'active' | 'archived'
}

const ChatListGrouped: React.FC<ChatListGroupedProps> = ({
  chats,
  currentChatId,
  loading,
  listType,
}) => {
  const groupChatsByDate = useCallback(
    (chats: Chat[]): GroupedChats => {
      const now = new Date()

      const grouped: GroupedChats = {
        Fixados: [],
        Hoje: [],
        Ontem: [],
        'Últimos 7 dias': [],
        'Últimos 30 dias': [],
      }

      const monthlyGroups: GroupedChats = {}

      chats.forEach(chat => {
        if (chat.isFixed && listType === 'active') {
          grouped['Fixados'].push(chat)
          return
        }

        const chatDate = new Date(chat.createdAt)
        const diffDays = Math.floor((now.getTime() - chatDate.getTime()) / (1000 * 60 * 60 * 24))

        if (diffDays === 0) {
          grouped['Hoje'].push(chat)
        } else if (diffDays === 1) {
          grouped['Ontem'].push(chat)
        } else if (diffDays <= 7) {
          grouped['Últimos 7 dias'].push(chat)
        } else if (diffDays <= 30) {
          grouped['Últimos 30 dias'].push(chat)
        } else {
          // Agrupa por mês/ano para chats mais antigos
          const monthYear = chatDate.toLocaleString('pt-BR', {
            month: 'long',
            year: 'numeric',
          })

          // Capitaliza a primeira letra do mês
          const formattedMonthYear = monthYear.charAt(0).toUpperCase() + monthYear.slice(1)

          if (!monthlyGroups[formattedMonthYear]) {
            monthlyGroups[formattedMonthYear] = []
          }
          monthlyGroups[formattedMonthYear].push(chat)
        }
      })

      const sortedMonthlyGroups = Object.keys(monthlyGroups)
        .sort((a, b) => {
          const dateA = new Date(a)
          const dateB = new Date(b)
          return dateB.getTime() - dateA.getTime()
        })
        .reduce((acc, key) => {
          acc[key] = monthlyGroups[key]
          return acc
        }, {} as GroupedChats)

      return { ...grouped, ...sortedMonthlyGroups }
    },
    [listType],
  )

  const renderGroupedChats = useMemo(() => {
    const groupedChats = groupChatsByDate(chats)

    return Object.entries(groupedChats).map(([groupName, groupChats]) => {
      if (groupChats.length === 0) return null

      return (
        <Fragment key={groupName}>
          <GroupTitle>
            {groupName === 'Fixados' && (
              <FontAwesomeIcon icon={faThumbTack} style={{ transform: 'rotate(45deg)' }} />
            )}
            {groupName}
            {groupName === 'Fixados' && (
              <FixedChatsCounter>
                {groupChats.length} / {MAX_FIXED_CHATS}
              </FixedChatsCounter>
            )}
          </GroupTitle>
          {groupChats.map(chat => (
            <ChatCard key={chat.id} isActive={currentChatId === chat.id} chat={chat} />
          ))}
        </Fragment>
      )
    })
  }, [chats, currentChatId, listType])

  if (loading && chats.length === 0) {
    return (
      <LoadingMessage>
        <PulseLoader size={8} color='#666' speedMultiplier={0.7} />
        Carregando chats
      </LoadingMessage>
    )
  }

  if (!chats || chats.length === 0) {
    return (
      <EmptyState>
        {listType === 'archived' ? (
          <>Nenhum chat arquivado.</>
        ) : (
          <>
            Nenhum chat ainda.
            <br />
            Comece uma conversa!
          </>
        )}
      </EmptyState>
    )
  }

  return <>{renderGroupedChats}</>
}

export default memo(ChatListGrouped)
